/**
 * Task Type Badge with Hover Tooltip
 *
 * Displays a task type badge with hover tooltip showing:
 * - Chinese name and description
 * - Update logic
 * - Affected tables
 * - Data sources
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { dataflowApi } from "@/services/api/dataflow";
import type { TaskHandler } from "@/types/dataflow";
import { useSmartTooltip, SmartTooltip } from "./SmartTooltip";

interface TaskTypeBadgeProps {
  taskType: string;
  variant?: "badge" | "text" | "select-option";
  className?: string;
}

export function TaskTypeBadge({ taskType, variant = "badge", className = "" }: TaskTypeBadgeProps) {
  const { data: handlers } = useQuery({
    queryKey: ["dataflow", "handlers"],
    queryFn: () => dataflowApi.getHandlers(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handler = handlers?.find((h: TaskHandler) => h.task_type === taskType);

  const {
    triggerRef,
    isVisible,
    computedPosition,
    actualPlacement,
    show,
    hide,
  } = useSmartTooltip({
    placement: "top",
    fallbackPlacements: ["bottom", "right", "left"],
    gap: 8,
  });

  const baseClasses = {
    badge: "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#1e3a5f] text-[#7dd3fc] border border-[#3b82f6]/30",
    text: "text-[#7dd3fc] cursor-help",
    "select-option": "",
  };

  if (variant === "select-option") {
    return (
      <div className="flex flex-col">
        <span className="text-[#e2e8f0]">{handler?.name || taskType}</span>
        {handler && (
          <span className="text-[#64748b] text-[10px] truncate">
            {handler.description.slice(0, 40)}...
          </span>
        )}
      </div>
    );
  }

  return (
    <>
      <span
        ref={triggerRef as React.RefObject<HTMLSpanElement>}
        className={`${baseClasses[variant]} ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {handler?.name || taskType}
      </span>

      {/* Smart Tooltip */}
      <SmartTooltip
        isVisible={isVisible && !!handler}
        position={computedPosition}
        placement={actualPlacement}
      >
        {handler && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#1e293b]">
              <span className="text-[#f8fafc] font-semibold text-sm">{handler.name}</span>
              <span className="text-[#64748b] text-[10px] px-1.5 py-0.5 bg-[#1a1d24] rounded">
                v{handler.version}
              </span>
            </div>

            {/* Description */}
            <div className="mb-3">
              <p className="text-[#94a3b8] text-xs leading-relaxed">
                {handler.description.length > 120
                  ? `${handler.description.slice(0, 120)}...`
                  : handler.description}
              </p>
            </div>

            {/* Update Logic */}
            {handler.update_logic && (
              <div className="mb-3">
                <h4 className="text-[#64748b] text-[10px] uppercase mb-1">更新逻辑</h4>
                <div className="text-[#94a3b8] text-[10px] leading-relaxed whitespace-pre-line bg-[#1a1d24] p-2 rounded">
                  {handler.update_logic.length > 200
                    ? `${handler.update_logic.slice(0, 200)}...`
                    : handler.update_logic}
                </div>
              </div>
            )}

            {/* Affected Tables */}
            {handler.affected_tables.length > 0 && (
              <div className="mb-2">
                <h4 className="text-[#64748b] text-[10px] uppercase mb-1">影响数据表</h4>
                <div className="flex flex-wrap gap-1">
                  {handler.affected_tables.map((table: string) => (
                    <span
                      key={table}
                      className="px-1.5 py-0.5 bg-[#1e3a5f]/50 text-[#7dd3fc] text-[10px] rounded"
                    >
                      {table}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Data Sources */}
            {handler.data_sources.length > 0 && (
              <div>
                <h4 className="text-[#64748b] text-[10px] uppercase mb-1">数据来源</h4>
                <div className="flex flex-wrap gap-1">
                  {handler.data_sources.map((source: string) => (
                    <span
                      key={source}
                      className="px-1.5 py-0.5 bg-[#1e293b] text-[#94a3b8] text-[10px] rounded"
                    >
                      {source}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Technical ID */}
            <div className="mt-3 pt-2 border-t border-[#1e293b]">
              <span className="text-[#475569] text-[10px] font-mono">{handler.task_type}</span>
            </div>
          </>
        )}
      </SmartTooltip>
    </>
  );
}

interface TaskTypeSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

// Delete tasks that should be hidden from template creation
const HIDDEN_TASK_TYPES = [
  "daily_delete",
  "fundamentals_delete",
  "wyckoff_delete",
  "quant_delete",
];

export function TaskTypeSelect({ value, onChange, className = "" }: TaskTypeSelectProps) {
  const { data: handlers } = useQuery({
    queryKey: ["dataflow", "handlers"],
    queryFn: () => dataflowApi.getHandlers(),
    staleTime: 5 * 60 * 1000,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [hoveredTaskType, setHoveredTaskType] = useState<string | null>(null);

  // Filter out delete tasks
  const visibleHandlers = handlers?.filter(
    (h: TaskHandler) => !HIDDEN_TASK_TYPES.includes(h.task_type)
  );

  const selectedHandler = visibleHandlers?.find((h: TaskHandler) => h.task_type === value);
  const hoveredHandler = visibleHandlers?.find((h: TaskHandler) => h.task_type === hoveredTaskType);

  const {
    triggerRef: panelTriggerRef,
    isVisible: panelVisible,
    computedPosition: panelPosition,
    actualPlacement: panelPlacement,
    show: showPanel,
    hide: hidePanel,
  } = useSmartTooltip({
    placement: "right",
    fallbackPlacements: ["left", "bottom", "top"],
    gap: 12,
  });

  // Connect hover state to smart tooltip
  const handleItemHover = (taskType: string | null) => {
    setHoveredTaskType(taskType);
    if (taskType) {
      showPanel();
    } else {
      hidePanel();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected Value Display */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 py-1.5 bg-[#1a1d24] border border-[var(--border)] rounded text-[#e2e8f0] text-xs text-left flex items-center justify-between"
      >
        <span>{selectedHandler?.name || value}</span>
        <span className="text-[#64748b]">▼</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-[#0f1115] border border-[var(--border)] rounded shadow-xl max-h-64 overflow-auto">
            {visibleHandlers?.map((handler: TaskHandler) => (
              <div
                key={handler.task_type}
                ref={hoveredTaskType === handler.task_type ? panelTriggerRef as React.RefObject<HTMLDivElement> : undefined}
                className={`px-3 py-2 cursor-pointer hover:bg-[#1a1d24] ${
                  handler.task_type === value ? "bg-[#1e3a5f]/30" : ""
                }`}
                onClick={() => {
                  onChange(handler.task_type);
                  setIsOpen(false);
                }}
                onMouseEnter={() => handleItemHover(handler.task_type)}
                onMouseLeave={() => handleItemHover(null)}
              >
                <div className="flex flex-col">
                  <span className="text-[#e2e8f0] text-xs">{handler.name}</span>
                  <span className="text-[#64748b] text-[10px] truncate">
                    {handler.description.slice(0, 50)}...
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Hover Detail Panel - Using Smart Tooltip */}
      <SmartTooltip
        isVisible={panelVisible && !!hoveredHandler && isOpen}
        position={panelPosition}
        placement={panelPlacement}
      >
        {hoveredHandler && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#1e293b]">
              <span className="text-[#f8fafc] font-semibold text-sm">{hoveredHandler.name}</span>
              <span className="text-[#64748b] text-[10px] px-1.5 py-0.5 bg-[#1a1d24] rounded">
                v{hoveredHandler.version}
              </span>
            </div>

            {/* Description */}
            <div className="mb-3">
              <p className="text-[#94a3b8] text-xs leading-relaxed">
                {hoveredHandler.description.length > 100
                  ? `${hoveredHandler.description.slice(0, 100)}...`
                  : hoveredHandler.description}
              </p>
            </div>

            {/* Update Logic */}
            {hoveredHandler.update_logic && (
              <div className="mb-3">
                <h4 className="text-[#64748b] text-[10px] uppercase mb-1">更新逻辑</h4>
                <div className="text-[#94a3b8] text-[10px] leading-relaxed whitespace-pre-line bg-[#1a1d24] p-2 rounded">
                  {hoveredHandler.update_logic.length > 150
                    ? `${hoveredHandler.update_logic.slice(0, 150)}...`
                    : hoveredHandler.update_logic}
                </div>
              </div>
            )}

            {/* Affected Tables & Data Sources */}
            <div className="grid grid-cols-2 gap-2">
              {hoveredHandler.affected_tables.length > 0 && (
                <div>
                  <h4 className="text-[#64748b] text-[10px] uppercase mb-1">数据表</h4>
                  <div className="flex flex-wrap gap-1">
                    {hoveredHandler.affected_tables.slice(0, 3).map((table: string) => (
                      <span
                        key={table}
                        className="px-1 py-0.5 bg-[#1e3a5f]/50 text-[#7dd3fc] text-[9px] rounded"
                      >
                        {table}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hoveredHandler.data_sources.length > 0 && (
                <div>
                  <h4 className="text-[#64748b] text-[10px] uppercase mb-1">数据源</h4>
                  <div className="flex flex-wrap gap-1">
                    {hoveredHandler.data_sources.slice(0, 3).map((source: string) => (
                      <span
                        key={source}
                        className="px-1 py-0.5 bg-[#1e293b] text-[#94a3b8] text-[9px] rounded"
                      >
                        {source}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SmartTooltip>
    </div>
  );
}
